import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MoreHorizontal, Search, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { type ChatChannelResponse, type ChatChannelMemberResponse } from '@/lib/api';
import { useArcheAvailability } from '@/hooks/useArcheAvailability';
import { SELF_DM_LABEL } from '../utils';
import { ChannelSettingsModal } from './ChannelSettingsModal';
import { ChannelSearchPanel } from './ChannelSearchPanel';
import { ChannelAvatar } from './ChannelAvatar';
import { ChannelMembersPopover } from './ChannelMembersPopover';

interface ChannelHeaderProps {
  channel: ChatChannelResponse | null;
  memberCount?: number;
  currentUserId?: string | null;
  members?: ChatChannelMemberResponse[];
  onBack?: () => void;
  onChannelLeft?: () => void;
  onChannelDeleted?: () => void;
  /**
   * Scroll the conversation to a message picked from search. Omit to render the
   * results read-only (surfaces without jump plumbing).
   */
  onJumpToMessage?: (messageId: string) => void;
  /** Opens a DM with a member picked from the member list. */
  onOpenDm?: (userId: string) => void | Promise<void>;
  /** Inserts an @mention for a member into this channel's composer. */
  onMentionMember?: (userId: string) => void;
  /**
   * Removes a member from this channel. Passed only when the viewer holds the
   * permission — the header does not authorize, it only offers.
   */
  onRemoveMember?: (userId: string) => void | Promise<void>;
}

export function ChannelHeader({
  channel,
  memberCount,
  currentUserId,
  members,
  onBack,
  onChannelLeft,
  onChannelDeleted,
  onJumpToMessage,
  onOpenDm,
  onMentionMember,
  onRemoveMember,
}: ChannelHeaderProps) {
  const guildsAvailable = useArcheAvailability('arche_guilds');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  if (!channel) {
    return (
      <div className="h-14 border-b border-border bg-card px-4 flex items-center">
        <span className="text-sm text-muted-foreground">Select a channel</span>
      </div>
    );
  }

  const isSelfDm = channel.isSelfDm === true;

  // For DM channels, prefer dmPartnerDisplayName from the server. A self-DM has
  // no partner — it's labeled "Saved Messages".
  let dmPartnerName: string | null = null;
  if (channel.channelType === 'direct' && !isSelfDm) {
    dmPartnerName = channel.dmPartnerDisplayName ?? channel.dmPartnerId ?? null;
    if (!dmPartnerName && currentUserId && members && members.length > 0) {
      const partner = members.find((m) => m.userId !== currentUserId);
      if (partner) dmPartnerName = partner.userId;
    }
  }

  const displayName = isSelfDm
    ? SELF_DM_LABEL
    : channel.channelType === 'direct'
      ? dmPartnerName || channel.name || 'Direct Message'
      : channel.name || 'Unnamed Channel';

  // Named channels read as "#general" in the search placeholder; DMs and
  // Saved Messages are already a person's name, so they stay bare.
  const searchLabel =
    channel.channelType === 'direct' || isSelfDm ? displayName : `#${displayName}`;

  return (
    <div className="h-14 border-b border-border bg-card px-4 flex items-center justify-between h-[55px]">
      <div className="flex items-center gap-3">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="md:hidden h-8 w-8 -ml-1 flex-shrink-0"
            aria-label="Back to channels"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        {/* Back to the guild this conversation belongs to. Keyed on guildId, not
            channelType, so a guild-scoped DM (type 'direct' with a guildId) is
            distinguishable from the personal DM with the same person. Absent for
            personal DMs, groups, and the global channel. */}
        {!!channel.guildId &&
          (guildsAvailable ? (
            <Link to={`/a/arche_guilds/${channel.guildId}`} className="flex-shrink-0">
              <Button variant="ghost" size="sm" className="h-8 px-2" aria-label="Back to guild">
                <Shield className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Back to Guild</span>
              </Button>
            </Link>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 flex-shrink-0"
              disabled
              aria-label="Back to guild"
            >
              <Shield className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Back to Guild</span>
            </Button>
          ))}
        <ChannelAvatar
          channel={channel}
          displayName={displayName}
          sizeClassName="h-8 w-8"
          bookmarkClassName="h-4 w-4"
          fallbackGlyph={
            channel.channelType === 'direct' ? displayName.charAt(0).toUpperCase() : '#'
          }
        />
        <div>
          <h2 className="text-sm font-semibold text-foreground">{displayName}</h2>
          {channel.description && (
            <p className="text-xs text-muted-foreground truncate max-w-xs">{channel.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        {/* The count doubles as the roster trigger — but only where a roster is
            a roster. A DM's "2 members" is the two people already named in the
            header, and the GLOBAL channel's membership is every account on the
            platform: turning that count into a browsable, actionable directory
            of the first 100 users is a different feature from "who is in this
            group", and not one this control should quietly become. */}
        {memberCount !== undefined &&
          (channel.channelType === 'group' || channel.channelType === 'guild' ? (
            <ChannelMembersPopover
              channelId={channel.channelId}
              members={members ?? []}
              memberCount={memberCount}
              currentUserId={currentUserId}
              onMessage={onOpenDm}
              onMention={onMentionMember}
              onRemoveMember={onRemoveMember}
            />
          ) : (
            <span>
              {memberCount} member{memberCount !== 1 ? 's' : ''}
            </span>
          ))}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSearchOpen(true)}
          className="h-8 w-8"
          title="Search messages"
          aria-label="Search messages"
        >
          <Search className="h-4 w-4" />
        </Button>
        {(channel.channelType === 'group' || channel.channelType === 'direct') && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSettingsOpen(true)}
            className="h-8 w-8"
            aria-label="Channel settings"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        )}
      </div>
      {/* Mounted only while open: the panel owns a search query, and a header
          that's always mounted shouldn't carry one per channel in the list. */}
      {searchOpen && (
        <ChannelSearchPanel
          channelId={channel.channelId}
          channelLabel={searchLabel}
          members={members}
          open
          onOpenChange={setSearchOpen}
          onSelectResult={onJumpToMessage}
        />
      )}
      {(channel.channelType === 'group' || channel.channelType === 'direct') && (
        <ChannelSettingsModal
          channel={channel}
          members={members ?? []}
          currentUserId={currentUserId}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          onLeft={onChannelLeft}
          onDeleted={onChannelDeleted}
        />
      )}
    </div>
  );
}
